/**
 * AdminHealth — single stoplight grid for the entire app's vital signs.
 *
 * The "is everything OK?" page you bookmark and check 3x a day. Each
 * dependency is a tile colored by status:
 *   - ok   → green
 *   - warn → amber (degraded but functional)
 *   - fail → red (broken)
 *   - unknown → gray (probably fine, just no recent signal)
 *
 * Tiles auto-refresh every 30s. The overall status banner at the top
 * matches the worst tile color. Drill-in links sit beneath every
 * actionable tile (e.g. "View errors" when an error rate is high).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, BellOff, CheckCircle2, Clock, CreditCard, Database,
  DollarSign, ExternalLink, HelpCircle, Loader2, Mail, RefreshCw, ShieldCheck,
  Sparkles, Users, XCircle, Zap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';


const TONE = {
  ok:      { bg: '#ecfdf5', border: '#10b981', fg: '#065f46', label: 'OK' },
  warn:    { bg: '#fffbeb', border: '#f59e0b', fg: '#92400e', label: 'WARN' },
  fail:    { bg: '#fef2f2', border: '#e11d48', fg: '#991b1b', label: 'FAIL' },
  unknown: { bg: '#f3f4f6', border: '#9ca3af', fg: '#4b5563', label: '—' },
};


const Icon = ({ status, size = 18 }) => {
  if (status === 'ok')   return <CheckCircle2 size={size} className="text-[#10b981]" />;
  if (status === 'warn') return <AlertTriangle size={size} className="text-[#f59e0b]" />;
  if (status === 'fail') return <XCircle size={size} className="text-[#e11d48]" />;
  return <HelpCircle size={size} className="text-[#9ca3af]" />;
};


const pickIcon = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('mongo')) return Database;
  if (n.includes('resend') || n.includes('mail')) return Mail;
  if (n.includes('stripe')) return CreditCard;
  if (n.includes('ai')) return Sparkles;
  if (n.includes('worker')) return Activity;
  return Zap;
};


const Tile = ({ tile }) => {
  const t = TONE[tile.status] || TONE.unknown;
  const TileIcon = pickIcon(tile.name);
  return (
    <div
      className="bg-white border-l-4 border border-[#e5e7eb] p-4 flex gap-3 items-start"
      style={{ borderLeftColor: t.border }}
      data-testid={`health-tile-${tile.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <TileIcon size={20} className="text-[#0f1115] flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="font-bold text-sm text-[#0f1115] truncate">{tile.name}</div>
          <span
            className="text-[10px] font-bold tracking-widest px-1.5 py-0.5"
            style={{ backgroundColor: t.bg, color: t.fg }}
          >
            {t.label}
          </span>
        </div>
        <div className="text-xs text-[#6b7280] leading-relaxed flex items-start gap-1.5">
          <Icon status={tile.status} size={12} />
          <span className="break-words">{tile.detail || '—'}</span>
        </div>
      </div>
    </div>
  );
};


const StatBlock = ({ label, value, sub, testId }) => (
  <div className="bg-white border border-[#e5e7eb] p-4" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-1">{label}</div>
    <div className="font-satoshi text-2xl font-black text-[#0f1115] tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-[#9ca3af] mt-0.5">{sub}</div>}
  </div>
);


const AdminHealth = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [quotas, setQuotas] = useState(null);
  const [seo, setSeo] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [stripeFails, setStripeFails] = useState(null);
  const [errorsRecent, setErrorsRecent] = useState(null);
  const [snooze, setSnooze] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const [h, a, q, s, c, sf, er, sn] = await Promise.all([
        axios.get(`${API}/admin/health`, { withCredentials: true }),
        axios.get(`${API}/admin/ai-usage?days=14`, { withCredentials: true }),
        axios.get(`${API}/admin/api-quotas`, { withCredentials: true }),
        axios.get(`${API}/admin/seo-pings?days=14`, { withCredentials: true }),
        axios.get(`${API}/admin/cohorts?weeks=8`, { withCredentials: true }),
        axios.get(`${API}/admin/stripe-failed-payments?limit=10`, { withCredentials: true }),
        axios.get(`${API}/admin/errors?hours=24`, { withCredentials: true }),
        axios.get(`${API}/admin/snooze`, { withCredentials: true }),
      ]);
      setData(h.data); setAi(a.data); setQuotas(q.data); setSeo(s.data);
      setCohorts(c.data); setStripeFails(sf.data); setErrorsRecent(er.data);
      setSnooze(sn.data);
    } catch (_e) {
      // soft-fail — partial tiles still render
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const id = setInterval(() => load(false), 30000);
    return () => clearInterval(id);
  }, [load]);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const overall = data?.overall || 'unknown';
  const overallTone = TONE[overall] || TONE.unknown;
  const tiles = data?.tiles || [];

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-health-page">
      <AdminReadinessBanner />
      <AdminSubNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Alert-snooze banner — appears at top of /admin/health whenever
            an admin has muted alerts. Tells the operator real alerts are
            being suppressed RIGHT NOW so they don't trust silence. */}
        {snooze?.active && snooze.snooze && (
          <div
            className="bg-[#fffbeb] border-l-4 border-[#f59e0b] p-4 mb-4 flex items-start gap-3"
            data-testid="health-snooze-banner"
          >
            <BellOff size={18} className="text-[#f59e0b] shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-[#92400e]">
                Alerts are snoozed
                {snooze.snooze.expires_at && (() => {
                  const ms = new Date(snooze.snooze.expires_at).getTime() - Date.now();
                  if (ms <= 0) return null;
                  const mins = Math.floor(ms / 60000);
                  const h = Math.floor(mins / 60);
                  const m = mins % 60;
                  return <span className="ml-2 text-[#92400e] font-normal">— expires in {h > 0 ? `${h}h ` : ''}{m}m</span>;
                })()}
              </div>
              <div className="text-[12px] text-[#92400e] mt-0.5">
                Reason: <em>{snooze.snooze.reason || 'manual'}</em> · set by{' '}
                <code className="font-mono">{snooze.snooze.set_by}</code>. All outbound
                alert emails (Stripe, readiness, error-spike, circuit-breaker) are silenced.
              </div>
            </div>
            <Link
              to="/admin/incidents"
              className="px-3 py-1.5 text-xs font-bold bg-white border border-[#f59e0b] text-[#92400e] hover:bg-[#fef3c7]"
              data-testid="health-snooze-manage-link"
            >
              Manage
            </Link>
          </div>
        )}

        {/* Overall banner */}
        <div
          className="border-l-4 border border-[#e5e7eb] bg-white p-4 mb-6 flex items-center justify-between"
          style={{ borderLeftColor: overallTone.border }}
          data-testid="health-overall-banner"
        >
          <div className="flex items-center gap-3">
            <Icon status={overall} size={28} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: overallTone.fg }}>
                System Health
              </div>
              <div className="font-satoshi text-2xl font-black text-[#0f1115]">
                {overall === 'ok' && 'All systems operational'}
                {overall === 'warn' && 'Degraded — drill in below'}
                {overall === 'fail' && 'Something is broken — drill in below'}
                {overall === 'unknown' && 'Insufficient signal yet'}
              </div>
              {data?.checked_at && (
                <div className="text-[11px] text-[#9ca3af] mt-0.5">
                  Checked {new Date(data.checked_at).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => load(false)}
            disabled={refreshing || loading}
            className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="health-refresh"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Updating…' : 'Refresh'}
          </button>
        </div>

        {/* Stoplight grid */}
        {loading ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center">
            <Loader2 size={24} className="animate-spin mx-auto text-[#6b7280]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="health-tile-grid">
            {tiles.map((tile, i) => <Tile key={i} tile={tile} />)}
          </div>
        )}

        {/* AI Usage */}
        {ai?.totals && (
          <section className="mb-8" data-testid="ai-usage-section">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
              <Sparkles size={18} className="text-[#002fa7]" />
              AI Burn-Rate · last {ai.window_days || 14} days
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBlock label="Total calls" value={ai.totals.calls?.toLocaleString() || 0} sub={`${ai.totals.error_rate_pct}% errors`} testId="ai-total-calls" />
              <StatBlock label="Est. tokens" value={ai.totals.estimated_tokens?.toLocaleString() || 0} sub="input-side" testId="ai-tokens" />
              <StatBlock label="Est. cost" value={`$${ai.totals.estimated_cost_usd || 0}`} sub="approx · calibrate w/ LLM bill" testId="ai-cost" />
              <StatBlock label="Avg latency" value={`${ai.totals.avg_latency_ms || 0}ms`} testId="ai-latency" />
            </div>
            {ai.pricing_note && (
              <div className="text-[11px] text-[#9ca3af] mt-2 italic">{ai.pricing_note}</div>
            )}
          </section>
        )}

        {/* Failed payments */}
        {stripeFails && (
          <section className="mb-8" data-testid="failed-payments-section">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
              <CreditCard size={18} className="text-[#e11d48]" />
              Recent Failed / Pending Payments
              <span className="text-xs font-normal text-[#6b7280]">— Stripe PaymentIntents not in succeeded state</span>
            </h2>
            <div className="bg-white border border-[#e5e7eb] p-4">
              {stripeFails.error && (
                <div className="text-sm text-[#e11d48]">{stripeFails.error}</div>
              )}
              {!stripeFails.error && (!stripeFails.intents || stripeFails.intents.length === 0) && (
                <div className="text-sm text-[#10b981]" data-testid="failed-payments-empty">
                  ✓ No failed or pending payments in the recent window.
                </div>
              )}
              {stripeFails.intents && stripeFails.intents.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#9ca3af]">
                      <th className="text-left py-1 px-2 font-semibold">PI ID</th>
                      <th className="text-left py-1 px-2 font-semibold">Status</th>
                      <th className="text-right py-1 px-2 font-semibold">Amount</th>
                      <th className="text-left py-1 px-2 font-semibold">Last error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stripeFails.intents.map((p) => (
                      <tr key={p.id} className="border-t border-[#e5e7eb]" data-testid={`pi-${p.id}`}>
                        <td className="py-2 px-2 font-mono text-xs">{p.id}</td>
                        <td className="py-2 px-2 font-semibold text-[#e11d48]">{p.status}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{p.currency} {p.amount?.toFixed(2)}</td>
                        <td className="py-2 px-2 text-xs text-[#6b7280] truncate max-w-md">{p.last_payment_error || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        {/* Errors quick-glance */}
        {errorsRecent && (
          <section className="mb-8" data-testid="errors-glance-section">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
              <AlertTriangle size={18} className={errorsRecent.total_errors > 0 ? 'text-[#e11d48]' : 'text-[#10b981]'} />
              Backend Errors · last 24h
              <Link to="/admin/errors" className="text-xs font-normal text-[#002fa7] hover:underline ml-auto" data-testid="link-errors-detail">
                View details →
              </Link>
            </h2>
            <div className="bg-white border border-[#e5e7eb] p-4">
              {errorsRecent.total_errors === 0 ? (
                <div className="text-sm text-[#10b981]">✓ No 5xx errors recorded in the last 24h.</div>
              ) : (
                <div>
                  <div className="font-satoshi text-3xl font-black text-[#e11d48] mb-2">{errorsRecent.total_errors}</div>
                  <div className="text-sm text-[#6b7280] mb-3">Top {Math.min(3, errorsRecent.groups?.length || 0)} groups:</div>
                  <ul className="space-y-1.5 text-sm">
                    {(errorsRecent.groups || []).slice(0, 3).map((g, i) => (
                      <li key={i} className="flex items-center gap-2 text-[#374151]">
                        <span className="font-mono text-xs bg-[#fef2f2] text-[#991b1b] px-1.5 py-0.5 font-bold">{g.count}×</span>
                        <span className="font-semibold">{g.exc_type}</span>
                        <span className="text-[#6b7280] truncate font-mono text-xs">{g.path}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Cohorts retention */}
        {cohorts?.cohorts && cohorts.cohorts.length > 0 && (
          <section className="mb-8" data-testid="cohorts-section">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
              <Users size={18} className="text-[#002fa7]" />
              Signup Cohort Retention
              <span className="text-xs font-normal text-[#6b7280]">— % active w1 / w2 / w4 after signup week</span>
            </h2>
            <div className="bg-white border border-[#e5e7eb] p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#9ca3af]">
                    <th className="text-left py-1 px-2 font-semibold">Cohort</th>
                    <th className="text-right py-1 px-2 font-semibold">Signups</th>
                    <th className="text-right py-1 px-2 font-semibold">Active W1</th>
                    <th className="text-right py-1 px-2 font-semibold">Active W2</th>
                    <th className="text-right py-1 px-2 font-semibold">Active W4</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.cohorts.map((r) => (
                    <tr key={r.cohort} className="border-t border-[#e5e7eb]" data-testid={`cohort-${r.cohort}`}>
                      <td className="py-2 px-2 font-mono">{r.cohort}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-semibold">{r.signups}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.active_w1} <span className="text-[#9ca3af]">({r.active_w1_pct}%)</span></td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.active_w2} <span className="text-[#9ca3af]">({r.active_w2_pct}%)</span></td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.active_w4} <span className="text-[#9ca3af]">({r.active_w4_pct}%)</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* API quotas */}
        {quotas?.providers && (
          <section className="mb-8" data-testid="quotas-section">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
              <DollarSign size={18} className="text-[#002fa7]" />
              External API Usage · today ({quotas.day})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quotas.providers.map((p) => (
                <div key={p.provider} className="bg-white border border-[#e5e7eb] p-4" data-testid={`quota-${p.provider}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold text-sm text-[#0f1115]">{p.label}</div>
                    {p.configured ? (
                      <span className="text-[10px] font-bold text-[#10b981]">CONFIGURED</span>
                    ) : (
                      <span className="text-[10px] font-bold text-[#e11d48]">KEY NOT SET</span>
                    )}
                  </div>
                  <div className="font-satoshi text-2xl font-black tabular-nums text-[#0f1115]">{(p.calls_today || 0).toLocaleString()}</div>
                  <div className="text-[11px] text-[#9ca3af]">calls today · {p.errors_today || 0} errors</div>
                  {p.last_error && (
                    <div className="text-[11px] text-[#e11d48] truncate mt-1">{p.last_error}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SEO crawler */}
        {seo && (
          <section className="mb-8" data-testid="seo-section">
            <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-3 flex items-center gap-2">
              <ExternalLink size={18} className="text-[#002fa7]" />
              Search Crawler Activity · last {seo.window_days || 14} days
              <span className="text-xs font-normal text-[#6b7280]">— bots fetching /sitemap.xml or /robots.txt</span>
            </h2>
            <div className="bg-white border border-[#e5e7eb] p-4">
              {Object.keys(seo.totals_by_bot || {}).length === 0 ? (
                <div className="text-sm text-[#9ca3af]">
                  No crawler hits captured yet. (Googlebot typically fetches /sitemap.xml every 1–7 days.)
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {Object.entries(seo.totals_by_bot).map(([bot, count]) => (
                    <div key={bot} className="border border-[#e5e7eb] px-3 py-2" data-testid={`bot-${bot}`}>
                      <div className="font-bold text-sm text-[#0f1115]">{bot}</div>
                      <div className="font-satoshi text-xl font-black tabular-nums">{count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminHealth;
